/**
 * What credits look like, everywhere they appear.
 *
 * One shape, three placements — the balance in the top bar, the price on a
 * feedback tile, the price on a submit button — so that a number with this dot
 * beside it always means the same thing. Getting that consistency from a
 * shared component rather than from three sets of matching classNames is the
 * point: the alternative drifts the first time one of them is restyled.
 *
 * ## Why a dot rather than an icon
 *
 * Every icon in `lib/icons` already means something here (people are the
 * panel, a route is a walk), so borrowing one
 * would make the price look like a feature. A filled dot in the accent lime is
 * the smallest mark that reads as currency and collides with nothing.
 */

/** Below this, the balance is stated in the alarm colour rather than in ink.
 *  Set to the cheapest thing that can be bought (`CANVAS_CREDITS.PERSONA_LOOKALIKES`
 *  at 10), because "low" is not a feeling — it is the point past which the
 *  product stops being able to do anything at all. */
export const LOW_BALANCE = 10;

export function CreditDot({ tone = 'accent' }: { tone?: 'accent' | 'alarm' }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'size-1.5 shrink-0 rounded-full',
        tone === 'alarm' ? 'bg-brand' : 'bg-highlight',
      ].join(' ')}
    />
  );
}

/**
 * A price, as it appears next to the thing being bought.
 *
 * `affordable={false}` states the price in the alarm colour instead of hiding
 * or disabling it — the number is the explanation, and a greyed-out price
 * tells somebody they cannot do a thing without telling them why.
 */
export function CreditCost({
  affordable = true,
  amount,
}: {
  affordable?: boolean;
  amount: number;
}) {
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center gap-1 rounded-pill px-2 py-0.5',
        // Tabular figures: these sit in a column on the tile grid, and
        // proportional digits make 20 and 100 look misaligned.
        'text-caption font-semibold tabular-nums',
        affordable ? 'bg-warm text-text-secondary' : 'bg-brand/10 text-brand',
      ].join(' ')}
      // The dot carries no meaning to a screen reader, so the unit is spelled
      // out here and hidden from sight below.
      aria-label={`${String(amount)} credits`}
    >
      <CreditDot tone={affordable ? 'accent' : 'alarm'} />
      <span aria-hidden="true">{amount}</span>
    </span>
  );
}
